/**
 * The five number fields of the Edit zone dialog, as text, turned into the PATCH body.
 *
 * <p>Pure, and separate from the dialog, for the reason every other module beside it is: the
 * interesting cases are arithmetic on strings a person is halfway through typing, and a component
 * test cannot show them all. A field that does not parse is <strong>omitted</strong> rather than
 * sent as `NaN` or as zero — and the difference matters most for `offline_release_days`, where 0 is
 * a real, meaningful setting ("never release") and must never be produced by an empty box.
 *
 * <p>The bounds are the server's and are not repeated here beyond the inputs' `min`/`max`: a 400
 * titled "Poll cadence out of range" or "Offline timers out of range" is rendered verbatim, so the
 * console can never disagree with the rule that is actually enforced.
 */

/** The dialog's raw field values, exactly as the inputs hold them. */
export interface ZoneTimerFields {
  pollIdle: string
  pollActive: string
  offlineAfter: string
  offlineReleaseDays: string
  secureFileTtl: string
}

/** The subset of `UpdateZoneRequest` these five fields own. */
export interface ZoneTimerPatch {
  poll_idle_seconds?: number
  poll_active_seconds?: number
  offline_after_seconds?: number
  offline_release_days?: number
  secure_file_ttl_seconds?: number
}

function parsed(value: string): number | undefined {
  const n = Number.parseInt(value, 10)
  return Number.isNaN(n) ? undefined : n
}

export function zoneTimerPatch(fields: ZoneTimerFields): ZoneTimerPatch {
  const idle = parsed(fields.pollIdle)
  const active = parsed(fields.pollActive)
  const after = parsed(fields.offlineAfter)
  const days = parsed(fields.offlineReleaseDays)
  const secureTtl = parsed(fields.secureFileTtl)
  return {
    ...(idle === undefined ? {} : { poll_idle_seconds: idle }),
    ...(active === undefined ? {} : { poll_active_seconds: active }),
    ...(after === undefined ? {} : { offline_after_seconds: after }),
    ...(days === undefined ? {} : { offline_release_days: days }),
    ...(secureTtl === undefined ? {} : { secure_file_ttl_seconds: secureTtl }),
  }
}
